import { useEffect } from 'react'

const SERVICE_WORKER_URL = '/service-worker.js'

/** Quita SW y caches de builds previos al correr `yarn dev`. */
export const unregisterStaleServiceWorkers = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) return

    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
        registrations.map((registration) => registration.unregister())
    )

    if ('caches' in window) {
        const cacheKeys = await caches.keys()
        await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    }
}

/**
 * Registra el service worker de la PWA.
 *
 * Antes lo inyectaba `@ducanh2912/next-pwa` (opción `register: true`) desde el
 * build de Webpack. Al generarse el service worker fuera del bundler para poder
 * usar Turbopack, la registración pasa a ser explícita.
 *
 * Solo corre en producción: en desarrollo no se genera `service-worker.js`,
 * igual que cuando next-pwa se deshabilitaba con `disable`.
 */
export const useRegisterServiceWorker = (): void => {
    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!('serviceWorker' in navigator)) return

        if (process.env.NODE_ENV !== 'production') {
            unregisterStaleServiceWorkers().catch((error) => {
                console.warn(
                    'Failed to unregister stale service workers:',
                    error
                )
            })
            return
        }

        const register = () => {
            navigator.serviceWorker
                .register(SERVICE_WORKER_URL, { scope: '/' })
                .catch((error) => {
                    console.error('Service worker registration failed:', error)
                })
        }

        // Se espera al load para no competir con los recursos de la primera vista.
        if (document.readyState === 'complete') {
            register()
            return
        }

        window.addEventListener('load', register)
        return () => {
            window.removeEventListener('load', register)
        }
    }, [])
}

export default useRegisterServiceWorker
